import { LogoSrc } from "./login-images";

type HotjarConfiguration =
  | {
      enabled: false;
    }
  | {
      enabled: true;
      id: number;
    };

type MixpanelConfiguration =
  | {
      enabled: false;
    }
  | {
      enabled: true;
      token: string;
      apiHost: string;
    };

type UnleashConfiguration =
  | { enabled: false }
  | {
      enabled: true;
      url: string;
      clientKey: string;
      refreshInterval: number;
      appName: string;
    };

export interface ApplicationProperties {
  hotjar: HotjarConfiguration;
  mixpanel: MixpanelConfiguration;
  settings: string[];
  login: string[];
  providers: string[];
  unleash: UnleashConfiguration;
  branding: { loginLogo: string; backgroundColor: string | undefined };
  tenantStyle: "corporate" | "informal";
}

const ENV_CONFIG: Record<string, ApplicationProperties> = {
  localhost: {
    hotjar: { enabled: false },
    mixpanel: { enabled: false },
    settings: ["password"],
    login: ["password", "oidc"],
    providers: ["microsoft", "google"],
    unleash: { enabled: false },
    branding: { loginLogo: LogoSrc.default, backgroundColor: "orange.light" },
    tenantStyle: "corporate",
  },
  "kantz.hooloovoo.dev": {
    hotjar: { enabled: true, id: 3066214 },
    mixpanel: {
      enabled: true,
      token: "fadbbdc2b518572a6e84e98a0d4313f3",
      apiHost: "https://api-eu.mixpanel.com",
    },
    settings: ["password", "oidc"],
    login: ["password", "oidc"],
    providers: ["google"],
    unleash: { enabled: false },
    branding: { loginLogo: LogoSrc.default, backgroundColor: "orange.light" },
    tenantStyle: "informal",
  },
  "kantz.hooloovoo.rs": {
    hotjar: { enabled: true, id: 3066200 },
    mixpanel: {
      enabled: true,
      token: "fadbbdc2b518572a6e84e98a0d4313f3",
      apiHost: "https://api-eu.mixpanel.com",
    },
    settings: [],
    login: ["oidc"],
    providers: ["google"],
    unleash: { enabled: false },
    branding: { loginLogo: LogoSrc.default, backgroundColor: "orange.light" },
    tenantStyle: "informal",
  },
  "kantz.app": {
    hotjar: { enabled: true, id: 3066214 },
    mixpanel: {
      enabled: true,
      token: "fadbbdc2b518572a6e84e98a0d4313f3",
      apiHost: "https://api-eu.mixpanel.com",
    },
    settings: ["password"],
    login: ["password", "oidc"],
    providers: ["microsoft"],
    unleash: { enabled: false },
    branding: { loginLogo: LogoSrc.default, backgroundColor: "orange.light" },
    tenantStyle: "informal",
  },
};

type TenantOverride = Pick<
  ApplicationProperties,
  "settings" | "login" | "providers" | "branding" | "tenantStyle"
>;

const TENANT_OVERRIDES: Record<string, TenantOverride> = {
  otp: {
    settings: ["password"],
    login: ["oidc", "password"],
    providers: ["microsoft"],
    branding: { loginLogo: LogoSrc.otp, backgroundColor: undefined },
    tenantStyle: "corporate",
  },
  hooloovoo: {
    settings: [],
    login: ["oidc"],
    providers: ["google"],
    branding: { loginLogo: LogoSrc.default, backgroundColor: "orange.light" },
    tenantStyle: "informal",
  },
};

export const Configuration: ApplicationProperties = resolveConfig();

export function isAccountSettingsVisible(): boolean {
  return Configuration.settings.length > 0;
}

function parseHostname(host: string): { base: string; tenant: string | null } {
  const bases = Object.keys(ENV_CONFIG);
  for (const base of bases) {
    if (host === base) return { base, tenant: null };
    if (host.endsWith("." + base)) {
      const tenant = host.slice(0, -base.length - 1);
      return { base, tenant };
    }
  }
  throw new Error(`Unknown domain '${host}'`);
}

export function resolveConfig(
  host = window.location.hostname
): ApplicationProperties {
  const { base, tenant } = parseHostname(host);
  const envConfig = ENV_CONFIG[base];
  const tenantOverride = tenant ? TENANT_OVERRIDES[tenant] : undefined;
  return tenantOverride ? { ...envConfig, ...tenantOverride } : envConfig;
}
